/**
 * Guide building blocks (Sep 2026): the Ask box, a rendered doc, and the
 * what's-new list. Used by the Guide page (/guide) and the per-page drawer.
 */
import { useState } from 'react';
import { ArrowUp, Sparkles } from 'lucide-react';
import type { GuideAskResponse, GuideDoc, WhatsNewEntry } from '@daaily/shared';
import { Icon } from '../design/Icon';
import { HubMark } from '../components/Loading';
import { renderMarkdown } from '../review/markdown';
import { callGuideAsk, dateLabel } from './guide';

export function GuideAsk({
  docId,
  docs,
  onOpenDoc,
  placeholder,
}: {
  docId?: string | null;
  docs: GuideDoc[];
  onOpenDoc: (id: string) => void;
  placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState('');
  const [res, setRes] = useState<GuideAskResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const titleOf = (id: string) => docs.find((d) => d.id === id)?.title ?? id;

  const ask = (question: string) => {
    const text = question.trim();
    if (text.length < 3 || busy) return;
    setBusy(true); setErr(null); setRes(null); setAsked(text);
    callGuideAsk({ q: text, docId: docId ?? null })
      .then((r) => { setRes(r.data); setQ(''); })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'The Guide could not answer right now.'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => { e.preventDefault(); ask(q); }}
        className="flex items-center gap-2 rounded-lg border border-line bg-surface pl-3 pr-1.5 focus-within:border-ring"
      >
        <Icon as={Sparkles} className="text-brand" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder ?? 'Ask the Guide, e.g. "Who approves my expenses?"'}
          maxLength={500}
          className="flex-1 h-11 !border-0 !bg-transparent !ring-0 !shadow-none px-0 text-sm focus:!outline-none"
          aria-label="Ask the Guide"
        />
        <button
          type="submit"
          disabled={busy || q.trim().length < 3}
          aria-label="Ask"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-fg disabled:opacity-40"
        >
          <Icon as={ArrowUp} />
        </button>
      </form>
      {busy && (
        <div className="flex items-center gap-2 text-sm text-fg-muted" role="status">
          <HubMark size={20} /> Reading the guide…
        </div>
      )}
      {err && <p className="text-sm text-danger">{err}</p>}
      {res && (
        <div className="rounded-lg border border-line bg-surface-2 p-4 space-y-3" aria-live="polite">
          <p className="text-xs text-fg-faint">You asked: {asked}</p>
          <div className="md-body text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(res.answer) }} />
          {res.sources.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-fg-faint">From:</span>
              {res.sources.map((s) => (
                <button key={s} type="button" onClick={() => onOpenDoc(s)} className="rounded-full border border-line bg-surface px-2 py-0.5 text-fg-muted hover:text-fg">
                  {titleOf(s)}
                </button>
              ))}
            </div>
          )}
          {res.followup && (
            <button type="button" onClick={() => ask(res.followup!)} className="text-xs text-brand underline underline-offset-2">
              {res.followup}
            </button>
          )}
          {!res.answerable && (
            <p className="text-xs text-fg-faint">The Guide doesn&rsquo;t cover this yet. Your question is logged so the docs can be improved.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function GuideDocBody({ doc, entries }: { doc: GuideDoc; entries: WhatsNewEntry[] }) {
  const recent = entries.filter((e) => e.docId === doc.id).slice(0, 3);
  return (
    <article className="space-y-6">
      {recent.length > 0 && (
        <section className="rounded-lg border border-line bg-surface p-4 space-y-2">
          <h3 className="text-2xs font-semibold uppercase text-fg-faint">Recently changed</h3>
          {recent.map((e) => (
            <div key={`${e.date}-${e.title}`}>
              <p className="text-sm font-medium text-fg">{e.title} <span className="font-normal text-fg-faint">· {dateLabel(e.date)}</span></p>
              <p className="text-sm text-fg-muted">{e.body}</p>
            </div>
          ))}
        </section>
      )}
      <div className="md-body guide-body text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(doc.body.replace(/^# .*\n+/, "")) }} />
      <p className="text-xs text-fg-faint">Last updated {dateLabel(doc.updated)}.</p>
    </article>
  );
}

export function WhatsNewList({
  entries,
  docs,
  seen,
  onOpenDoc,
}: {
  entries: WhatsNewEntry[];
  docs: GuideDoc[];
  seen: string;
  onOpenDoc: (id: string) => void;
}) {
  if (entries.length === 0) return <p className="text-sm text-fg-muted">Nothing new for your apps yet.</p>;
  const titleOf = (id: string) => (id === 'hub' ? 'Hub' : docs.find((d) => d.id === id)?.title ?? id);
  return (
    <ol className="space-y-3">
      {entries.map((e) => (
        <li key={`${e.date}-${e.docId}-${e.title}`} className="rounded-lg border border-line bg-surface p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-fg-faint">
            {e.date > seen && <span className="rounded-full bg-brand-subtle text-brand px-2 py-0.5 font-semibold">New</span>}
            <span>{dateLabel(e.date)}</span>
            <span aria-hidden="true">·</span>
            {e.docId === 'hub'
              ? <span>{titleOf(e.docId)}</span>
              : <button type="button" onClick={() => onOpenDoc(e.docId)} className="underline underline-offset-2 hover:text-fg">{titleOf(e.docId)}</button>}
          </div>
          <p className="mt-1 text-sm font-medium text-fg">{e.title}</p>
          <p className="text-sm text-fg-muted">{e.body}</p>
        </li>
      ))}
    </ol>
  );
}
